import Alert from '@storeblocks/alert';
import { Button } from '@storeblocks/button';
import { Input } from '@storeblocks/input';
import Modal from '@storeblocks/modal';
import {
  Field,
  FieldInputProps,
  FieldMetaProps,
  Form,
  FormikErrors,
} from 'formik';
import { PropsWithChildren, useEffect, useRef } from 'react';

import { Show } from '@/components/Show';
import { WithGap } from '@/components/WithGap';
import { useTexts } from '@/hooks/useTexts';
import { TrackingElement, useGetTrackId } from '@/tracking';

import { textInputSettings } from './UserFormSchema';

type CurrentMode =
  | 'editOrganization'
  | 'editAll'
  | 'addOrganization'
  | 'addAll';

interface Props<T> extends PropsWithChildren {
  show: boolean;
  onClose: () => void;
  header: string;
  isSubmitting: boolean;
  submitFailed: boolean;
  currentMode: CurrentMode;
  errors: FormikErrors<T>;
  dirty: boolean;
  trackingModal: string;
  children?: React.ReactNode;
}

interface FormFieldRenderProps {
  field: FieldInputProps<string>;
  meta: FieldMetaProps<string>;
}

export const UserAdminForm = <T,>({
  show,
  onClose,
  header,
  isSubmitting,
  submitFailed,
  currentMode,
  errors,
  dirty,
  children,
  trackingModal,
}: Props<T>): JSX.Element => {
  const texts = useTexts();
  const getTrackId = useGetTrackId();

  const alertRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (submitFailed && alertRef.current) {
      alertRef.current.scrollIntoView();
    }
  }, [submitFailed, alertRef]);

  return (
    <Form id={`${currentMode}-form`}>
      <Modal
        trigger={undefined}
        open={show}
        onClose={onClose}
        title={header}
        content={
          <WithGap>
            <WithGap direction="row">
              <Field name="givenName">
                {({ field, meta }: FormFieldRenderProps) => (
                  <Input
                    {...field}
                    disabled={
                      textInputSettings.givenName.disabledRules[currentMode]
                    }
                    label={texts.givenName}
                    fluid
                    alert={{
                      messageType:
                        meta.touched && meta.error ? 'error' : undefined,
                      message: meta.touched ? meta.error : undefined,
                    }}
                  />
                )}
              </Field>
              <Field name="familyName">
                {({ field, meta }: FormFieldRenderProps) => (
                  <Input
                    {...field}
                    disabled={
                      textInputSettings.familyName.disabledRules[currentMode]
                    }
                    label={texts.familyName}
                    fluid
                    alert={{
                      messageType:
                        meta.touched && meta.error ? 'error' : undefined,
                      message: meta.touched ? meta.error : undefined,
                    }}
                  />
                )}
              </Field>
            </WithGap>
            <Field name="email">
              {({ field, meta }: FormFieldRenderProps) => (
                <Input
                  {...field}
                  disabled={textInputSettings.email.disabledRules[currentMode]}
                  label={texts.email}
                  fluid
                  alert={{
                    message: meta.touched ? meta.error : undefined,
                    messageType:
                      meta.touched && meta.error ? 'error' : undefined,
                  }}
                />
              )}
            </Field>
            {children}

            <Show when={submitFailed}>
              <Alert
                ref={alertRef}
                variant="error"
                description={texts.admin.user.couldNotSave}
              />
            </Show>
          </WithGap>
        }
        footer={
          <WithGap gap="16" direction="row" key="modal-button-container">
            <Button
              type="submit"
              data-test="save-button"
              disabled={
                isSubmitting || !dirty || Object.keys(errors).length > 0
              }
              form={`${currentMode}-form`}
              data-trackid={getTrackId(
                `save-changes:${trackingModal}-modal`,
                TrackingElement.Button,
              )}
              loading={isSubmitting}
            >
              {texts.adminModal.saveButton}
            </Button>
            <Button
              type="button"
              data-test="cancel-button"
              disabled={isSubmitting}
              variant="subtle"
              onClick={onClose}
              data-trackid={getTrackId(
                `cancel:${trackingModal}-modal`,
                TrackingElement.Button,
              )}
            >
              {texts.admin.cancel}
            </Button>
          </WithGap>
        }
      ></Modal>
    </Form>
  );
};
